/**
 * Iteratively deep flattens an object.
 *
 * @copyright 2020 Aleksej Komarov
 * @license MIT
 * @param obj Input object
 */
export function deepFlattenObject(obj: any) {
  const flattenedObj: { [k: string]: any } = {};
  // Queue of key value pairs that need to be visited.
  let toVisit: [any, any][] = [[undefined, obj]];
  const hasOwn = Object.prototype.hasOwnProperty;

  while (toVisit.length > 0) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore // TODO: FIXME Types. This worked before, but after migration types are showing errors
    const [key, value] = toVisit.shift();

    if (typeof value === 'object' && value !== null) {
      // If it's an empty array - it's already the lowest level
      if (Array.isArray(value) && value.length === 0) {
        flattenedObj[key] = value;
        continue;
      }

      // Welp, in case we have a translation, we assign it just like that.
      // We love exceptions to the rules, don't we?
      const translation = value.props?.translation;

      if (key !== undefined && translation) {
        flattenedObj[key] = translation;
        continue;
      }

      // We iterate an object this way regardless of whether it is an Array
      // or Object, because an Array can have its *own* assigned properties.
      const tempArr: [string, any][] = [];

      for (const i in value) {
        if (hasOwn.call(value, i)) {
          tempArr.push([
            // If the visited key is undefined, this means that we're
            // currently at root level
            key === undefined ? i : `${key}.${i}`,
            value[i],
          ]);
        }
      }

      // We are doing this to keep same order of original object
      toVisit = [...tempArr, ...toVisit];

      continue;
    }

    // If we don't have a key, we can't really assign anything.
    if (key === undefined) {
      continue;
    }

    // We are left with a primitive, non-traversable value, assign it.
    flattenedObj[key] = value;
  }

  return flattenedObj;
}
